import * as THREE from 'three';

/* ═══════════════════════════════════════════════════════════════════════════
   Warm-up des shaders — et surtout son FILET (PLAN.md §4.8).

   Un shader n'est compilé qu'au premier draw de l'objet qui l'utilise, avec un
   readback GPU bloquant. Sans préchauffage : 100 à 500 ms de freeze à la
   première apparition de chaque type d'ennemi, de chaque arme, du boss. C'est
   à dire exactement aux moments que le jeu met en scène.

   Trois contraintes, dans cet ordre, et l'ordre compte :

     1. L'ÉCLAIRAGE ET L'ENVIRONNEMENT d'abord. Le nombre de lumières et la
        présence d'une envMap sont des `#define` : les poser après le warm-up
        invaliderait toutes les variantes déjà compilées.
     2. `compileAsync`, jamais `compile`. La version asynchrone s'appuie sur
        KHR_parallel_shader_compile et NE BLOQUE PAS le thread — la barre de
        chargement continue de bouger.
     3. Un FILET AUTOMATISÉ. Un warm-up ne peut pas être supposé acquis, il doit
        être MESURÉ : on relève `renderer.info.programs.length` après le
        préchauffage, et toute croissance ultérieure signale une variante
        oubliée. C'est la différence entre un espoir et un test.

   Bonne nouvelle vérifiée dans la doc de three : `compile()` traverse le graphe
   via `Object3D.traverse()`, qui ne teste ni `visible` ni le frustum culling.
   Les objets n'ont donc pas besoin d'être visibles — seulement présents.
   ═══════════════════════════════════════════════════════════════════════════ */

export class Warmup {
  private baseline = -1;

  /**
   * Compile toutes les variantes présentes dans les scènes.
   *
   * ⚠️ Les lots instanciés doivent avoir un `count > 0` À CET INSTANT. Un
   * InstancedMesh vide est ignoré par le renderer : rien n'est compilé, le
   * warm-up rend fièrement son chiffre, et les programmes apparaissent un par
   * un pendant la première vague — c'est-à-dire exactement le freeze qu'on
   * prétendait supprimer. Voir `View.primeForWarmup()`.
   */
  async compile(
    renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    camera: THREE.Camera,
    extraScenes: readonly THREE.Scene[] = [],
  ): Promise<void> {
    await renderer.compileAsync(scene, camera);
    for (const s of extraScenes) await renderer.compileAsync(s, camera);
  }

  /**
   * Fige la référence. À appeler APRÈS une frame complète passée par la vraie
   * chaîne de rendu — composer inclus.
   *
   * Les passes de post-processing compilent leurs propres programmes au premier
   * `composer.render()`. Prendre la référence avant les compterait comme des
   * fuites à la frame suivante, et le filet crierait au loup en permanence
   * jusqu'à ce qu'on cesse de le regarder.
   */
  mark(renderer: THREE.WebGLRenderer): number {
    this.baseline = renderer.info.programs?.length ?? 0;
    return this.baseline;
  }

  /** Nombre de programmes relevé juste après le warm-up. */
  get programCount(): number {
    return this.baseline;
  }

  /**
   * Le filet. Retourne le nombre de programmes apparus DEPUIS le warm-up.
   *
   * Toute valeur non nulle en cours de partie signifie qu'une variante de
   * matériau a échappé au préchauffage — et donc qu'un freeze s'est produit,
   * ou va se produire, chez le joueur. C'est le test que le plan exige au
   * jalon J3 : « programsAfterFirstWave === programsAfterWarmup ».
   */
  leaked(renderer: THREE.WebGLRenderer): number {
    if (this.baseline < 0) return 0;
    const now = renderer.info.programs?.length ?? 0;
    return now - this.baseline;
  }

  /** Un objet apparu en cours de partie — cas rare, mais il existe. Le 3ᵉ
   *  paramètre de `compileAsync` sert exactement à ça : compiler un nouvel
   *  objet dans le contexte d'éclairage de la scène vivante. */
  async compileLate(
    renderer: THREE.WebGLRenderer,
    object: THREE.Object3D,
    camera: THREE.Camera,
    liveScene: THREE.Scene,
  ): Promise<void> {
    await renderer.compileAsync(object, camera, liveScene);
    this.baseline = renderer.info.programs?.length ?? this.baseline;
  }
}
